import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { Spinner, Alert, Form } from "react-bootstrap";

const transformData = (data, period) => {
  const result = [];
  const uniqueDates = [...new Set(data.map((item) => item.date))];
  const uniquePartners = [...new Set(data.map((item) => item.partner_name))];

  uniqueDates.forEach((date) => {
    const entry = { date };

    uniquePartners.forEach((partner) => {
      const partnerData = data.find(
        (item) => item.date === date && item.partner_name === partner
      );
      entry[partner] = partnerData ? partnerData.total_sales : 0;
    });

    result.push(entry);
  });

  if (period === "month") {
    // Группировка данных по месяцам
    const monthlyResult = [];
    result.forEach((entry) => {
      const month = entry.date.slice(0, 7); // Формат YYYY-MM
      const existing = monthlyResult.find((e) => e.date === month);

      if (!existing) {
        const newEntry = { date: month };
        uniquePartners.forEach((partner) => {
          newEntry[partner] = entry[partner] || 0;
        });
        monthlyResult.push(newEntry);
      } else {
        uniquePartners.forEach((partner) => {
          existing[partner] += entry[partner] || 0;
        });
      }
    });
    return monthlyResult;
  }

  // Если выбрана группировка по дням
  return result;
};

const PartnersSalesChart = ({ startDate, endDate }) => {
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState("day"); // Период: day или month

  const apiUrl = process.env.REACT_APP_API_URL;

  useEffect(() => {
    if (!startDate || !endDate) return;
    const fetchChartData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(
          `${apiUrl}/admin/analytics/top-partners-sales-over-time?start_date=${startDate}&end_date=${endDate}`,
          {
            credentials: "include",
          }
        );
        if (!response.ok) {
          throw new Error("Ошибка загрузки данных");
        }
        const rawData = await response.json();
        const transformedData = transformData(rawData, period);
        setChartData(transformedData);
      } catch (error) {
        console.error("Ошибка загрузки данных:", error);
        setError("Не удалось загрузить данные");
      } finally {
        setLoading(false);
      }
    };
    fetchChartData();
  }, [apiUrl, startDate, endDate, period]);

  return (
    <div>
      <div className="d-flex justify-content-between align-items-baseline mb-3 flex-wrap gap-2">
        <div>
          <h3 className="mb-0">Продажи партнёров</h3>
          <p className="text-muted small mb-0">
            Период: {startDate} — {endDate}
          </p>
        </div>
        <Form.Group controlId="period" className="d-flex align-items-center gap-2">
          <Form.Label className="mb-0">Группировка</Form.Label>
          <Form.Select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="w-auto"
          >
            <option value="day">По дням</option>
            <option value="month">По месяцам</option>
          </Form.Select>
        </Form.Group>
      </div>

      {loading ? (
        <div className="text-center mt-4">
          <Spinner animation="border" variant="primary" />
          <p>Загрузка данных...</p>
        </div>
      ) : error ? (
        <Alert variant="danger">{error}</Alert>
      ) : chartData.length === 0 ? (
        <Alert variant="info">Нет данных для отображения</Alert>
      ) : (
        <div className="card p-4 shadow-sm">
          <ResponsiveContainer width="100%" height={400}>
            <LineChart
              data={chartData}
              margin={{
                top: 20,
                right: 30,
                left: 20,
                bottom: 50,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 12 }}
                angle={-30}
                height={60}
              />
              <YAxis />
              <Tooltip />
              <Legend />
              {Object.keys(chartData[0])
                .filter((key) => key !== "date")
                .map((partner) => (
                  <Line
                    key={partner}
                    type="monotone"
                    dataKey={partner}
                    stroke={`#${Math.floor(Math.random() * 16777215).toString(
                      16
                    )}`}
                    strokeWidth={2}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default PartnersSalesChart;
PartnersSalesChart.propTypes = {
  startDate: PropTypes.string.isRequired,
  endDate: PropTypes.string.isRequired,
};
